import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { PopupData, Service, Timeframe, ValidationErrors } from 'webapp-types';
import { GoalType, TimeframeType, UserAccess } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';
import FormRadiobuttonBoxGroupComponent from 'webapp/components/form/radiobutton-box-group';
import GoalInlineGroupComponent from 'webapp/components/goal/inline-group';
import GoalInlineOwnerComponent from 'webapp/components/goal/inline-owner';
import GoalsTimeframeDropdownComponent from 'webapp/components/goals/timeframe/dropdown';
import GoalsVisibilityInlineComponent from 'webapp/components/goals/visibility/inline';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

export default class GoalPopupCreateComponent extends PopupInnerModalComponent<PopupData.Goal.Create> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/groups') declare mmGroupsService: Service.ModelManager.Groups;
	@service('account') declare accountService: Service.Account;

	@tracked errors: ValidationErrors<'title'> = {};

	visibilityList = [
		{
			value: UserAccess.PUBLIC,
			label: 'All users',
		},
		{
			value: UserAccess.RESTRICTED,
			label: 'Me and selected users',
		},
		{
			value: UserAccess.PRIVATE,
			label: 'Only me',
		},
	];

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.NEW_GOAL, {
			origin: this.popupData.origin,
		});

		void this.mmGroupsService.requestGroups();
	}

	get model() {
		return this.popupData.model;
	}

	get usersList() {
		return this.mmUsersService.activatedUsersList;
	}

	get isGoalTypeObjectiveDisabled() {
		return this.popupData.isGoalTypeObjectiveDisabled;
	}

	get goalTypeList() {
		const isOKRsDisabled = !this.mmGoalsService.isGoalsOKRsEnabled,
			isGoalNotPublic = !this.model.isPublic,
			isObjectiveTypeDisabled =
				this.isGoalTypeObjectiveDisabled || isOKRsDisabled || isGoalNotPublic;

		const goalIcon: IconName = 'Target',
			objectiveIcon: IconName = 'Flag';

		return [
			{
				value: GoalType.GOAL,
				label2: 'Specific, measurable step',
				label: 'Goal',
				icon: goalIcon,
			},
			{
				value: GoalType.OBJECTIVE,
				label: 'Objective',
				label2: 'High-level, strategic outcome',
				icon: objectiveIcon,
				isDisabled: isObjectiveTypeDisabled,
				showAddon: isOKRsDisabled,
				tooltip: this.tooltipText,
			},
		];
	}

	get isObjectiveTypeSelected() {
		return this.model.isObjectiveType;
	}

	get isContinuousDisabled() {
		return this.isObjectiveTypeSelected || this.isCreatingSubgoal;
	}

	get continuousDisabledTooltip(): string {
		if (this.isCreatingSubgoal) {
			return "Continuous goals can't be added as sub-goal.";
		}
		return '';
	}

	get tooltipText(): string {
		if (!this.mmGoalsService.isGoalsOKRsEnabled) {
			const hideDataboxContent =
				this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
			let text =
				'Break down strategic objectives into measurable goals, and track progress automatically.';
			if (!hideDataboxContent) {
				text +=
					' <a href="https://databox.com/okr-and-forecast-modeling-launch" target="_blank">Learn more</a>';
			}
			return text;
		} else if (!this.model.isPublic) {
			return 'Objectives can only be public.';
		} else {
			return this.isGoalTypeObjectiveDisabled
				? "This goal type isn't available with your current settings."
				: '';
		}
	}

	get isCreatingSubgoal() {
		return !!this.popupData.isSubgoal;
	}

	get isVisibilityDisabled() {
		return this.isObjectiveTypeSelected || this.isCreatingSubgoal;
	}

	get visibilityTooltip(): string {
		if (this.isCreatingSubgoal) {
			return 'Goals that have sub-goals or are added as sub-goals must be public.';
		}
		if (this.isObjectiveTypeSelected) {
			return 'Objectives can only be public.';
		}
		return '';
	}

	@action
	changeGoalType(value: AnySimpleValue): void {
		const goalTypeOption = value as GoalType;
		if (this.model.type === goalTypeOption) {
			return;
		}

		this.model.type = goalTypeOption;

		if (this.model.isTimeframeContinuous) {
			this.model.timeframe = {
				type: TimeframeType.Y,
				year: new Date().getFullYear(),
			};
		}
	}

	@action
	changeTimeframe(timeframe: Timeframe | null): void {
		this.model.timeframe = timeframe;
		this.model.historyValues = {};

		this.model.checkSuccessCondition(true);
		this.model.checkPrimaryGranulation();
	}

	@action
	changeOwner(value: number): void {
		this.model.ownerId = value;
	}

	@action
	next(): void {
		if (!this.validate()) {
			return;
		}

		this.close();

		// passing the same model
		this.mmGoalsService.openEditPopup(
			this.model,
			this.popupData.origin,
			this.popupData.onGoalSaved,
			undefined,
			this.popupData.onlyAutomaticEnabled,
			true,
			this.popupData.isGoalTypeObjectiveDisabled,
			this.popupData.isSubgoal,
		);
	}

	private validate() {
		const errors: ValidationErrors<'title'> = {};

		if (!this.model.title) {
			errors.title = [{ message: 'Title is required' }];
		}

		this.errors = errors;

		return Object.keys(errors).length === 0;
	}
	<template>
		<div class="title">
			<CommonUiIconComponent @name="OkRs" @size="lg" />
			New goal or objective
		</div>

		<div class="content">
			<div class="form-column">
				<span class="label">Type</span>
				<FormRadiobuttonBoxGroupComponent
					@options={{this.goalTypeList}}
					@selected={{this.model.type}}
					@onChange={{this.changeGoalType}}
					@extraClass="form-row"
				/>
			</div>
			<div class="form-row">
				<div class="form-column goal-title">
					<span class="label">Title</span>
					<FormInputComponent
						name="title"
						placeholder="Grow sales pipeline"
						maxlength="80"
						@type="text"
						@value={{this.model.title}}
						@hideFloatLabel={{true}}
					/>
					<RowErrorComponent @items={{this.errors.title}} />
				</div>
			</div>
			<div class="form-row">
				<div class="form-column goal-owner">
					<span class="label">Owner</span>
					<GoalInlineOwnerComponent @model={{this.model}} @canEdit={{true}} />
				</div>
				<div class="form-column">
					<span class="label">Company or accountable team</span>
					<GoalInlineGroupComponent @model={{this.model}} @canEdit={{true}} />
				</div>
			</div>
			<div class="form-row">
				<div class="form-column">
					<span class="label">Timeframe</span>
					<GoalsTimeframeDropdownComponent
						@model={{this.model}}
						@onChange={{this.changeTimeframe}}
						@isContinuousDisabled={{this.isContinuousDisabled}}
						@continuousDisabledTooltip={{this.continuousDisabledTooltip}}
					/>
				</div>
				<div class="form-column goal-visibility">
					<span class="label">Privacy</span>
					<GoalsVisibilityInlineComponent
						@model={{this.model}}
						@isDisabled={{this.isVisibilityDisabled}}
						@saveOnChange={{false}}
						@tooltipText={{this.visibilityTooltip}}
					/>
				</div>
			</div>
			<div class="controls2">
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Next"
						@extraClass="save"
						@onTrigger={{this.next}}
					/>
				</div>
			</div>
		</div>
	</template>
}
